import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ACTIVITY_METRICS, activityTotal, type BoardPerson, type MetricSelection } from '@/lib/team-pulse-board'

export function ActivityComparison({ people, selection }: { people: BoardPerson[]; selection: MetricSelection }) {
  const defs = ACTIVITY_METRICS.filter(m => selection === 'all' || m.key === selection)
  const data = people.map(person => {
    const total = activityTotal(person, selection)
    return {
      id: person.id, name: person.name,
      ...Object.fromEntries(defs.map(def => [def.key, total.included.find(m => m.key === def.key)?.value ?? 0])),
      total: total.value ?? 0,
      label: total.value === null ? '未計測' : `${total.value.toLocaleString('ja-JP')}${selection === 'all' ? '件' : defs[0].unit}${total.partial ? '（確認分）' : ''}`,
    }
  })
  const max = Math.max(1, ...data.map(d => d.total))
  const step = 10 ** Math.floor(Math.log10(max)) / 2
  const domainMax = Math.ceil(max / step) * step
  if (!people.length) return <p className="tp-empty">活動データに人物が登録されていません。</p>
  return <>
    <div className="tp-chart-scroll" aria-label="活動件数の比較グラフ">
      <div className="tp-chart" style={{ height: Math.max(160, people.length * 30 + 24) }} aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart key={selection} layout="vertical" data={data} margin={{ top: 8, bottom: 0, left: 0, right: 122 }} accessibilityLayer={false}>
            <CartesianGrid horizontal={false} stroke="#e1e7ef" strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, domainMax]} allowDecimals={false} tick={{ fontSize: 11, fill: '#52627d' }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={108} tick={{ fontSize: 13, fill: '#142039' }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: '#f0f7ff' }} content={({ active, payload }) => {
              const person = active ? people.find(p => p.id === payload?.[0]?.payload.id) : null
              return person ? <div className="tp-chart-tooltip"><strong>{person.name}</strong>{defs.map(def => {
                const measure = person.measures.find(m => m.key === def.key)!
                const reason = measure.reason ?? (selection === 'all' ? measure.overallReason : null)
                return <p key={def.key}>{def.label}：{measure.value === null ? '未計測' : `${measure.value}${def.unit}`}{reason && <small>{reason}</small>}</p>
              })}</div> : null
            }} />
            {defs.map((def, i) => <Bar key={def.key} dataKey={def.key} stackId="activity" fill={def.color} barSize={20} isAnimationActive={false}>
              {i === defs.length - 1 && <LabelList dataKey="label" position="right" style={{ fontSize: 12, fill: '#142039' }} />}
            </Bar>)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
    <details className="tp-chart-values">
      <summary>件数と比較対象外の理由を見る</summary>
      <p>色に頼らず各指標の件数を確認できます。</p>
      <div className="tp-data-scroll">
        <table><caption className="sr-only">活動実績の内訳</caption><thead><tr><th scope="col">氏名</th>{defs.map(d => <th key={d.key} scope="col">{d.label}</th>)}<th scope="col">合計</th></tr></thead>
          <tbody>{people.map(person => {
            const total = activityTotal(person, selection)
            return <tr key={person.id}><th scope="row">{person.name}</th>
              {defs.map(def => {
                const measure = person.measures.find(m => m.key === def.key)!
                const excluded = measure.reason ?? (selection === 'all' ? measure.overallReason : null)
                return <td key={def.key}>{measure.value === null ? '未計測' : `${measure.value.toLocaleString('ja-JP')}${def.unit}`}{excluded && <span className="tp-exclusion">{excluded}{measure.value === null && measure.savedValue !== null ? `（参考保存値 ${measure.savedValue}）` : ''}</span>}</td>
              })}<td>{total.value === null ? '未計測' : total.value.toLocaleString('ja-JP')}{total.partial && total.value !== null ? '（確認分）' : ''}</td></tr>
          })}</tbody>
        </table>
      </div>
    </details>
  </>
}
